import { Button, Field, Input, Stack, Text } from '@chakra-ui/react'
import { useMutation } from '@tanstack/react-query'
import { useRef, useState, type FormEvent } from 'react'
import { Link as RouterLink } from 'react-router'
import { api } from '~/api/client'
import { ActionBar } from '~/components/member/ActionBar'
import { ErrorAlert, useFriendlyError } from '~/components/member/feedback'
import { ArtHeading } from '~/components/member/Illustration'
import { FormStack, MemberLayout } from '~/components/member/layout'
import { useT } from '~/i18n/use-t'

const FORM_ID = 'forgot-form'

/**
 * Forgotten password (passwordless spec §4, P8): the answer is the same whether or not the email belongs to a voter.
 * An account that may sign in by email gets the sign-in email (a link and a code); an account that asks for the
 * password and a code gets the link to choose a new password. The page says both, since it cannot know which.
 */
export default function Forgot() {
  const { t } = useT()
  const friendly = useFriendlyError()
  const [email, setEmail] = useState('')
  const [missing, setMissing] = useState(false)
  const emailRef = useRef<HTMLInputElement>(null)
  const send = useMutation({ mutationFn: () => api('memberPasswordForgot', { body: { email: email.trim() } }) })
  const submit = (e: FormEvent) => {
    e.preventDefault()
    setMissing(!email.trim())
    if (email.trim()) send.mutate()
    else emailRef.current?.focus()
  }
  return (
    <MemberLayout backTo='/login'>
      <ArtHeading title={t('forgot.title')} art='password' />
      {send.isSuccess ? (
        <Stack gap={4}>
          <Text role='status'>{t('forgot.done')}</Text>
          <Text>{t('forgot.doneReset')}</Text>
          <ActionBar>
            <Button asChild size='lg' w='full' variant='outline'>
              <RouterLink to='/login'>{t('forgot.toLogin')}</RouterLink>
            </Button>
          </ActionBar>
        </Stack>
      ) : (
        <FormStack id={FORM_ID} gap={5} onSubmit={submit} noValidate>
          <Text>{t('forgot.text')}</Text>
          <Field.Root invalid={missing}>
            <Field.Label>{t('login.email')}</Field.Label>
            <Input
              ref={emailRef}
              type='email'
              autoComplete='email'
              inputMode='email'
              autoCapitalize='none'
              spellCheck={false}
              enterKeyHint='done'
              value={email}
              onChange={(e) => {
                setEmail(e.target.value)
                setMissing(false)
              }}
            />
            <Field.ErrorText>{missing ? t('login.errEmail') : null}</Field.ErrorText>
          </Field.Root>
          {send.isError && <ErrorAlert>{friendly(send.error)}</ErrorAlert>}
          <ActionBar>
            <Button type='submit' form={FORM_ID} size='lg' w='full' loading={send.isPending}>
              {t('forgot.button')}
            </Button>
          </ActionBar>
        </FormStack>
      )}
    </MemberLayout>
  )
}
